import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getJobHistory } from '../api';
import type { JobHistoryItem } from '../api';
import { getJobUrl } from '../utils/jobLinks';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  ACTIVATION: 'Set up workspace',
  GENERATE_IMAGES: 'Generated images',
  GENERATE_VIDEOS: 'Created videos',
  CREATE_OUTLINES: 'Planned content',
  CREATE_CONTENT: 'Wrote content',
  REGENERATE_CONTENT: 'Rewrote content',
  SUPERVISOR: 'Reviewed pipeline',
  BRAIN: 'Brain response',
  CHAT: 'Responded',
  ONBOARDING: 'Activation',
};

function getLabel(action: string): string {
  return (
    ACTION_LABELS[action] ??
    action.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
  );
}

// Sweeper-marked jobs carry this prefix — see LOST_JOB_ERROR_PREFIX in
// backend/ai_agent/tasks.py. We render a friendlier hint for these so the
// user understands the run was interrupted (likely a deploy) and can retry.
const LOST_JOB_ERROR_PREFIX = 'Job interrupted';

function getFailureHint(job: Pick<JobHistoryItem, 'status' | 'last_error'>): string | null {
  if (job.status !== 'failed') return null;
  if (!job.last_error) return null;
  if (job.last_error.startsWith(LOST_JOB_ERROR_PREFIX)) {
    return 'Interrupted — please retry';
  }
  return job.last_error;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workspaceId: number;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AIRunsLogModal({ workspaceId, onClose }: Props) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getJobHistory(workspaceId, 50)
      .then((res) => setJobs(res.jobs ?? []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  function handleJobClick(job: JobHistoryItem) {
    const url = getJobUrl(job);
    if (url) {
      navigate(url);
      onClose();
    }
  }

  const content = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>AI Run History</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body" style={{ padding: '0', maxHeight: 480, overflowY: 'auto' }}>
          {loading && (
            <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              Loading…
            </p>
          )}
          {!loading && jobs.length === 0 && (
            <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No completed runs yet.
            </p>
          )}
          {!loading && jobs.length > 0 && (
            <ul className="ai-runs-log__list">
              {jobs.map((job) => {
                const url = getJobUrl(job);
                const clickable = Boolean(url);
                const hint = getFailureHint(job);
                return (
                  <li
                    key={job.job_id}
                    className={`ai-runs-log__item${clickable ? ' ai-runs-log__item--link' : ''}`}
                    onClick={() => handleJobClick(job)}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => (e.key === 'Enter' || e.key === ' ') && handleJobClick(job)
                        : undefined
                    }
                    title={hint ?? undefined}
                  >
                    <span
                      className={`ai-runs-log__status-icon ai-runs-log__status-icon--${job.status}`}
                      aria-hidden="true"
                    >
                      {job.status === 'completed' ? '✓' : '✗'}
                    </span>
                    <span className="ai-runs-log__label">
                      {getLabel(job.action)}
                      {hint && (
                        <span
                          className="ai-runs-log__hint"
                          style={{
                            display: 'block',
                            fontSize: '0.85em',
                            color: 'var(--text-muted)',
                            marginTop: 2,
                          }}
                        >
                          {hint}
                        </span>
                      )}
                    </span>
                    <span className="ai-runs-log__time">{formatRelativeTime(job.created_at)}</span>
                    {clickable && (
                      <span className="ai-runs-log__arrow" aria-hidden="true">
                        →
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
